import {put, call, all} from 'redux-saga/effects';
import Matter from "../../entities/matter";
import * as Actions from "../actions/matterActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type MatterAction = {
    matterId: string;
    matter: Matter;
    matters: Matter[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneMatter(action: MatterAction) {
	const session = tabSession.captureGeneration();
	try {
		const matter: Matter = yield call(api.findOne, 'matter', action.matterId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.MATTER_FETCH_ONE_SUCCESS, matterId: action.matterId, matter, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTER_FETCH_ONE_FAILURE, matterId: action.matterId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {MatterAction} action
 */
export function* saveMatter(action: MatterAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ MATTER_SAVE saga', action.matter?.id);

		if (action.matter === undefined || action.matter === null) {
			console.error('saveMatter received a null');
			throw new Error('saveMatter received a null');
		}

		if (!(action.matter instanceof Matter)) {
			action.matter = new Matter(action.matter);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.matter.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.matter);
			const addResult = yield call(idb.add, 'matter', action.matter);
			if (addResult.isErr()) {
				console.error(`⮂ saveMatter: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertMatterSave(action.matter, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.MATTER_SAVE_FAILURE, matter: action.matter, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.MATTER_SAVE, matter: action.matter, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertMatterSave(action.matter, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.MATTER_SAVE_FAILURE, matter: action.matter, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {MatterAction} action
 * @param {Matter[]} action.matters
 */
export function* saveManyMatters(action: MatterAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ MATTER_SAVE_MANY saga', action.matters.map(matter => matter.id).join(','));

        if (!Array.isArray(action.matters)) {
            console.error('saveManyMatters expected an array, received', action.matters);
            throw new Error('saveManyMatters expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveMatter), under these queue keys
		const dispatchedAddedOns: number[] = action.matters.map(matter => matter?.addedOn);
		const revertAll = () => action.matters.map((matter, i) =>
			put(Actions.revertMatterSave(matter, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Matter[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.matters.length; i++) {
				const matter = action.matters[i];
				matter.persisted = false;
				ensureOperationId(matter);
				toSave.push(matter);
			}

			const addResult = yield call(idb.add, 'matter', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyMatters: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.MATTER_SAVE_MANY_FAILURE, matters: action.matters, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.MATTER_SAVE_MANY, matters: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.MATTER_SAVE_MANY_FAILURE, matters: action.matters, status: 500, err});

			console.error(`Error saving ${action.matters.length} matters to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {MatterAction} action
 */
export function* removeMatter(action: MatterAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.matter === undefined || action.matter === null || !(action.matter instanceof Matter)) {
			console.error('removeMatter expected type Matter, received', action.matter);
			reportQueued(false);
			throw new Error('removeMatter expected type Matter');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.matter.status = 'D';
		action.matter.addedOn = newHRTimestamp();
		action.matter.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.matter);
		const addResult = yield call(idb.add, 'matter', action.matter);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeMatter: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.MATTER_REMOVE_FAILURE, matter: action.matter, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTER_REMOVE, matter: action.matter});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTER_REMOVE_FAILURE, matter: action.matter, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Matter.
	*
	* @param {MatterAction} action
	*/
export function* restoreMatter(action: MatterAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreMatter expected getCurrentUserId() to return a value');
        return;
    }

	if (action.matter === undefined || action.matter === null) {
        console.error('restoreMatter expected a Matter, received null');
        throw new Error('restoreMatter expected a Matter, received null');
    }
    else if (!(action.matter instanceof Matter)) action.matter = new Matter(action.matter); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.matter.status = 'U';
		action.matter.addedOn = newHRTimestamp();
		action.matter.addedBy = userId;
		action.matter.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.matter);
		const addResult = yield call(idb.add, 'matter', action.matter);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreMatter: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTER_RESTORE_FAILURE, matter: action.matter, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTER_RESTORE, matter: action.matter});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTER_RESTORE_FAILURE, matter: action.matter, status: 500, err});

		console.error('Error restoring from database', err);
	}
}